import { useState } from 'react'
import IFAQ from 'models/IFAQ'

export interface FAQProps {}

enum Tabs {
  General = 'General',
  Account = 'Account',
  Billing = 'Billing',
}

export const useFAQ = (props: FAQProps) => {
  const [activeTab, setActiveTab] = useState(Tabs.General)

  const generalFAQs: IFAQ[] = [
    {
      question: 'How do I create a new account?',
      answer: {
        __html:
          'To create a new account:<ol><li>Choose your plan — select between Free Forever, Plus, or Pro.</li><li>Fill out the registration form with your name, restaurant name, email address, and phone number.</li><li>Accept the Terms and Conditions and Privacy Policy, then click Confirm.</li><li>Verify your email address via the confirmation link sent to your inbox.</li></ol><ul><li>If you selected the Free Forever plan, you’ll get instant access to your dashboard, where you can immediately start creating your online menu.</li><li>If you selected one of the paid plans (Plus or Pro), a member of our team will contact you to complete the payment process before activating your dashboard access.</li></ul>',
      },
    },
    {
      question: 'How can customers view my menu?',
      answer: {
        __html:
          'Once your account is activated and you log in to your dashboard, you can access the “My QR Code” page from the navigation bar. From there, you can download your restaurant’s QR code, which links directly to your online menu. You can then display this QR code at your location or share it digitally with customers.',
      },
    },
    {
      question: 'Can I use my own branding?',
      answer: {
        __html:
          'Yes! You can upload your own logo, background image, and even set your restaurant’s custom color palette from the admin panel under Settings > My Restaurant.',
      },
    },
  ]

  const accountFAQs: IFAQ[] = [
    {
      question: 'How do I add menu categories and items?',
      answer: {
        __html:
          'Use the admin panel: <ol><li>Go to Categories to add main sections.</li><li>Go to Sub Categories to organize sections under each category.</li><li>Go to Products to add food items with name, price, image, calories, and ingredients.</li></ol>',
      },
    },
    {
      question: 'Can I update my restaurant info later?',
      answer: {
        __html:
          'Yes, all details like phone numbers, location, Wi-Fi password, currency, restaurant description, open hours, and color palette can be updated anytime under Settings > My Restaurant.',
      },
    },
    {
      question: 'Is there support for multiple branches?',
      answer: {
        __html:
          'Multi-branch support is available in the Pro Plan. Each branch can be managed with its own details under the same account.',
      },
    },
  ]

  const billingFAQs: IFAQ[] = [
    {
      question: 'What are the available plans and pricing?',
      answer: {
        __html:
          'There are three plans: <ol><li>Free Forever: Limited categories and items.</li><li>Plus – $79/year: Unlimited items, branding features.<li>Pro – $149/year: All features, including AI menu assistant (soon) and multi-branch support</li></ol>',
      },
    },
    {
      question: 'What are the payment methods that we accept?',
      answer: {
        __html:
          'We accept Whish, OMT, Western Union, and cash as payment methods.',
      },
    },
    {
      question: 'How do I upgrade my plan?',
      answer: {
        __html:
          'Go to your admin dashboard > My Subscription and select the desired plan.',
      },
    },
    {
      question: 'What happens if my subscription expires?',
      answer: {
        __html:
          'If your subscription expires: <ul><li>Your menu will no longer be accessible to customers via the QR code or public link.</li><li>You will not be able to log in to your dashboard or make any changes.</li><li>All your data will be securely stored under your account for up to 6 months.</li><li>If you renew your subscription within 6 months, your data will be restored and everything will work as before.</li><li>If you do not renew within 6 months, all your data will be permanently deleted. If you subscribe again after that, you will need to start from scratch and re-enter all your restaurant information and menu data.</li></ul>',
      },
    },
  ]

  const handleActiveTab = (tab: Tabs) => {
    setActiveTab(tab)
  }

  return {
    ...props,
    Tabs,
    activeTab,
    generalFAQs,
    accountFAQs,
    billingFAQs,
    handleActiveTab,
  }
}
